import axios from 'axios'
import moment from 'moment'
import 'moment/locale/ru'
import {getToken, removeToken, ensureValidToken, isRefreshInFlight, isOAuth2User, adoptPeerAccessToken} from "./methods/saveToken";
import { accessIssuedAt, requestPeerAccessToken } from "./methods/authTabSync";
import {
  note401DuringRefresh,
  shouldRetry401WithRefresh,
  trackAuthSessionEvent,
} from "./methods/authSessionTelemetry";
import { getGlobalAppContext } from './contexts/AppContext';
import { isMissingDataError } from './methods/missingDataError';

export function nError(error: any) {
  if (isMissingDataError(error)) {
    return {
      error,
      notification: {
        title: 'Не хватает данных',
        content: error.message,
        sticky: true,
      }
    }
  }

  // HTTP error (axios)
  if (error.response) {
    console.warn(error.request, error.response)
    return {
      error,
      notification: {
        title: `Ошибка №${error.response.status}`,
        content: `${error.response.data}`
      }
    }
  }

  // Another error
  else if (error.code == "ERR_NETWORK") {
    return {
      error,
      notification: {
        title: 'Возникла ошибка',
        content: 'Проверьте наличие соединения с интернетом или попробуйте попытку позже'
      }
    }
  } else if (error.code === "ECONNABORTED") {
    return {
      error,
      notification: {
        title: 'Превышено время ожидания',
        content: 'Проверьте интернет-соединение и попробуйте отправить код ещё раз'
      }
    }
  } else {
    console.error(error)
  }

  // Return error content
  return {
    error,
    notification: {
      title: 'Возникла ошибка',
      content: error.message
    }
  }
}

export function preparePhoneNumber(str: string): string {
  str = str.replace(/[^\d]/g, '')
  if (str.length === 11 && str[0] === '8') {
    str = '7' + str.slice(1)
  }

  if (str.length <= 10)
    return str

  return '+' + str
}

/** Номер для звонка (Plusofon call-to-auth): человекочитаемый формат. */
export function formatDialPhoneForDisplay(dialPhone: string): string {
  const digits = (dialPhone || '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('8')) {
    return `8 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`
  }
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`
  }
  return dialPhone
}

/** tel:-ссылка для клика «позвонить» с телефона. */
export function dialPhoneTelUri(dialPhone: string): string {
  const digits = (dialPhone || '').replace(/\D/g, '')
  if (!digits) {
    return ''
  }
  if (digits.length === 11 && digits.startsWith('8')) {
    return `tel:${digits}`
  }
  return `tel:+${digits}`
}

export function isBeelinePhoneNumber(phone_number: string): boolean {
  if (!phone_number) {
    return false
  }

  try {
    const prepared = preparePhoneNumber(phone_number)
    
    // Проверяем, что номер начинается с +7
    if (!prepared.startsWith('+7')) {
      return false
    }
    
    // Проверяем длину (должно быть 12 символов для +7XXXXXXXXXX)
    if (prepared.length !== 12) {
      return false
    }
    
    // Получаем три цифры после +7 (код оператора)
    const operatorCode = prepared.slice(2, 5)
    
    // Коды Билайна
    const beelineCodes = ['903', '905', '906', '909', '960', '961', '962', '963', '964', '965', '967']
    
    return beelineCodes.includes(operatorCode)
  } catch {
    return false
  }
}

export function isRegistrationSmsCountry(phone_number: string): boolean {
  if (!phone_number) {
    return false
  }
  try {
    const prepared = preparePhoneNumber(phone_number)
    const digits = prepared.replace(/\D/g, '')
    if (digits.startsWith('375')) {
      return true // BY
    }
    // +7… → РФ или КЗ (как detect_jurisdiction_by_phone на бэке)
    if (digits.startsWith('7')) {
      return true
    }
    return false
  } catch {
    return false
  }
}

function isRegistrationFreeSmsPurpose(purpose?: string): boolean {
  // Только чистая регистрация без договора. register_and_sign — через пакет signing_sms.
  return purpose === 'register'
}

export function isSMSAllowedForPhoneNumber(
  phone_number: string,
  disabled_code_channels: string[] = [],
  purpose?: string,
) {
  if (!phone_number) {
    return false
  }

  try {
    // Проверяем, является ли номер Билайна
    if (isBeelinePhoneNumber(phone_number)) {
      return false // SMS запрещены для номеров Билайна
    }

    preparePhoneNumber(phone_number)

    // Чистая регистрация: SMS для РФ/КЗ/РБ без пакета signing_sms
    if (isRegistrationFreeSmsPurpose(purpose) && isRegistrationSmsCountry(phone_number)) {
      return true
    }

    // auth / sign / register_and_sign: SMS включается пакетом signing_sms
    // (sms попадает в disabled_code_channels, если апгрейда нет).
    return !disabled_code_channels.includes('sms')
  } catch {
    return false
  }
}

export function isWhatsAppAllowedForPhoneNumber(phone_number: string, disabled_code_channels: string[] = []) {
  if (!phone_number || disabled_code_channels.includes('whatsapp')) {
    return false
  }
  try {
    const prepared = preparePhoneNumber(phone_number)
    const digits = prepared.replace(/\D/g, '')
    return digits.length >= 10
  } catch {
    return false
  }
}

export function isCallAllowedForPhoneNumber(phone_number: string, disabled_code_channels: string[] = []) {
  return !disabled_code_channels.includes('call')
}

export function isReverseCallAllowedForPhoneNumber(phone_number: string, disabled_code_channels: string[] = []) {
  if (disabled_code_channels.includes('reverse-call')) {
    return false
  }
  try {
    return rfOrRBPhoneNumber(phone_number)
  } catch {
    return false
  }
}
export function isTGAllowedForPhoneNumber(phone_number: string, disabled_code_channels: string[] = []) {
  return !disabled_code_channels.includes('tg')
}
export function isTGGatewayAllowedForPhoneNumber(phone_number: string, disabled_code_channels: string[] = []) {
  return !disabled_code_channels.includes('tg-gateway')
}

export function prepareSeveralPhoneNumbers(str: string): string {

  str = str.replace(/[^\d]/g, '')
  if (str.length === 11 && str[0] === '8') {
    str = '7' + str.slice(1)
  }

  // 10 цифр: код из 2 цифр и 8 национальных (Дания, Франция, Норвегия)
  if (/^(45|33|47)\d{8}$/.test(str)) {
    return '+' + str
  }

  if (str.length <= 10)
    throw new Error("Неправильный номер телефона")

  return '+' + str
}

export const httpClient = makeClient()

/** Хуки для локального Playwright E2E (oauth2 refresh / single-flight). */
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  ;(window as any).__OKIDOKI_E2E__ = {
    httpClient,
    ensureValidToken,
    getToken,
    isRefreshInFlight,
  }
}

export function makeGuestHttpClient() {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
  })
}

function makeClient() {
  let client = axios.create({
    baseURL: process.env.REACT_APP_API_URL
  })
  let token = getToken()
  if (token) {
    client.defaults.headers['Authorization'] = `Bearer ${token}`
  }
  
  // Добавляем глобальный обработчик ошибок для автоматического обновления токенов
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      console.log("HTTP interceptor error:", error)
      const originalRequest = error.config || {}
      const requestUrl = String(originalRequest.url || '')

      if (error?.response?.status === 401 && isRefreshInFlight()) {
        note401DuringRefresh(requestUrl)
        trackAuthSessionEvent('token_401_during_refresh', { url: requestUrl })
      }

      // Сам /refresh_token, сброс cookie и помеченные запросы не гоняем в цикл refresh
      if (
        originalRequest.skipAuthRefresh ||
        requestUrl.includes('/user/refresh_token') ||
        requestUrl.includes('/user/clear_refresh_cookie')
      ) {
        return Promise.reject(error)
      }

      // Если получили 401 и это не повторный запрос
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true

        const errorText = error?.response?.data || error?.message || ''

        if (shouldRetry401WithRefresh(isOAuth2User(), errorText)) {
          console.log("OAuth2 401 in interceptor, attempting to refresh...")

          const beforePeer = getToken()
          const peer = await requestPeerAccessToken()
          if (peer && accessIssuedAt(peer) > accessIssuedAt(beforePeer)) {
            adoptPeerAccessToken(peer)
            const recovered = getToken()
            if (recovered) {
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers['Authorization'] = `Bearer ${recovered}`
              return client(originalRequest)
            }
          }

          let refreshOk = false
          try {
            const appContext = getGlobalAppContext()
            refreshOk = await ensureValidToken(appContext, { force: true })

            if (refreshOk) {
              console.log("Token refreshed successfully in interceptor")
              const newToken = getToken()
              if (newToken) {
                originalRequest.headers = originalRequest.headers || {}
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`
                return client(originalRequest)
              }
            }
          } catch (refreshError) {
            console.log("Failed to refresh token in interceptor:", refreshError)
          }

          if (!refreshOk) {
            removeToken('interceptor_refresh_fail', { url: requestUrl })
          }
        }
      }

      return Promise.reject(error)
    }
  );
  
  return client
}

export function toHumanTime(seconds: any) {
  return new Date(seconds * 1000).toISOString().substr(14, 5)
}

export function rand(min: number, max: number): number {
  return Math.round(Math.random() * (max - min) + min)
}

// Initiate moment
moment.locale('ru')
export const $moment = moment

export function rfOrRBPhoneNumber(phone_number: string) {
  if (!phone_number) {
    return false
  }

  try {
    let prepared_phone_number = preparePhoneNumber(phone_number)
    switch (prepared_phone_number.length) {
      case 12:
        let rfPrefix = prepared_phone_number.slice(0, 3)
        return rfPrefix == '+79'
      case 13:
        let rbPrefix = prepared_phone_number.slice(0, 4)
        return rbPrefix == '+375'
      default:
        return false
    }
  } catch {
    return false
  }
}
